import { ImportContactsContentType, IImpersonationContext, IOperationResultNoValue, IUser } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../models/AppState';
import { AppImportContactsViewModel } from '../../../viewmodels/AppViewModels';
import { ContactsSelfImportMapping } from '../../../web/components/contacts/ContactsSelfImportMapping';
import { ContactsSelfImportProcessing } from '../../../web/components/contacts/ContactsSelfImportProcessing';
import { FilesDropTarget } from '../../../web/components/FilesDropTarget';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Modal } from '../../../web/components/Modal';
import { SpreadsheetIcon } from '../../../web/components/svgs/icons/SpreadsheetIcon';
import { SvgIcon } from '../../../web/components/svgs/icons/SvgIcon';
import { ITabViewChild, TabView } from '../../../web/components/TabView';
import { brandSecondary } from '../../../web/styles/colors';
import { ContactImportUploadOptions } from './ContactImportUploadOptions';
import { ConvertToLevitate } from './ConvertToLevitate';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps {
	className?: string;
	impersonationContext?: IImpersonationContext;
	isOpen?: boolean;
	onRequestClose?: any;
	showDeleteRenewalKeyFactsOption?: boolean;
	user?: IUser;
}

enum Page {
	Importing,
	Initial,
	Loader,
	Map,
	PickFile,
}

interface IState {
	csvFile?: File;
	dataRefresh: boolean;
	page: Page;
	selectedTabIndex?: number;
	visibility?: string;
}

export const AcceptedFileExtensions = '.csv,.xlsx,.xls';

const acceptedFileExtensionList = AcceptedFileExtensions.split(',').map(e => e.slice(1).toLocaleLowerCase());

function isAcceptedFileExtension(ext: string | null): boolean {
	return !!ext && acceptedFileExtensionList.includes(ext.toLocaleLowerCase());
}

const tabViewItems = [
	{
		className: css(styleSheet.importTabbarItem),
		content: 'Import',
		dataContext: 'Import',
	},
	{
		className: css(styleSheet.importTabbarItem),
		content: 'Convert',
		dataContext: 'Convert',
	},
];

class _ContactImport extends React.Component<IProps, IState> {
	private readonly filesInputRef = React.createRef<HTMLInputElement>();
	private mImportContacts: AppImportContactsViewModel;

	public readonly state: IState = {
		csvFile: null,
		dataRefresh: false,
		page: Page.Initial,
		visibility: null,
	};

	constructor(props: IProps) {
		super(props);

		this.mImportContacts = props.user
			? new AppImportContactsViewModel(props.userSession, props.user.accountId, props.user.id)
			: undefined;
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
		if (!!nextProps.user && !!nextProps.user.id) {
			if (!this.props.user || this.props.user.id !== nextProps.user.id) {
				this.mImportContacts = new AppImportContactsViewModel(
					nextProps.userSession,
					nextProps.user.accountId,
					nextProps.user.id
				);
			}
			this.setState({ csvFile: null, dataRefresh: false, page: Page.Initial, visibility: null });
		} else if (!nextProps.user && !!this.props.user) {
			// reset
			this.mImportContacts = undefined;
			this.setState({ csvFile: null, dataRefresh: false, page: Page.Initial, visibility: null });
		} else if (this.props.isOpen !== nextProps.isOpen) {
			if (!nextProps.isOpen) {
				this.mImportContacts = undefined;
			}
			this.setState({ csvFile: null, dataRefresh: false, page: Page.Initial, visibility: null });
		}
	}

	public render() {
		return (
			<Modal
				className={css(styleSheet.modal)}
				isOpen={!!this.props.isOpen}
				onRequestClose={this.onClose}
				useDefaultHeader
			>
				{this.renderBody()}
			</Modal>
		);
	}

	private renderBody() {
		const { page } = this.state;

		switch (page) {
			case Page.Loader:
				return <LoadingSpinner className='absolute-center' type='large' />;

			case Page.PickFile:
				return this.renderPickFile();

			case Page.Map:
				if (!this.mImportContacts) {
					return null;
				}

				return (
					<ContactsSelfImportMapping
						className={css(styleSheet.csvImportMapping)}
						importContacts={this.mImportContacts}
						onCancelButtonClicked={this.onCancel}
						onImportClicked={this.onImportClick}
						showMergeByNameCheckbox={true}
					/>
				);

			case Page.Importing:
				return (
					<ContactsSelfImportProcessing
						bySupportUser={true}
						importContacts={this.mImportContacts}
						onRequestClose={this.onClose}
					/>
				);

			default:
				return (
					<TabView
						contentClassName={css(styleSheet.importTabbarContent)}
						key='tab'
						onSelectedTabIndexChanged={this.onSelectedTabIndexChanged}
						selectedTabIndex={this.state.selectedTabIndex}
						tabbarClassName={css(styleSheet.importTabbar)}
					>
						{this.getTabViewContent}
					</TabView>
				);
		}
	}

	private renderPickFile() {
		if (!this.mImportContacts) {
			return null;
		}

		const uploadHint = `Supported file types: ${AcceptedFileExtensions.split(',').reduce((acc, y) => {
			return `${acc}${acc ? ', ' : ''}${y}`;
		}, '')}`;
		const visibilityLabel = !!this.state.visibility && this.state.visibility === 'admin' ? 'Private' : 'Shared';

		const optionsBlock = (
			<ContactImportUploadOptions
				dataRefresh={this.state.dataRefresh}
				impersonationContext={this.props.impersonationContext}
				importContacts={this.mImportContacts}
				onDataRefreshChange={this.onDataRefreshChange}
				showDataRefreshOption={this.props.showDeleteRenewalKeyFactsOption}
			/>
		);

		if (!this.state.csvFile) {
			return (
				<div className={css(styleSheet.modalContainer)}>
					<h2 className={css(styleSheet.header)}>Import Contacts</h2>
					<p>Drag a file into the box below or browse your computer (visibility: {visibilityLabel}).</p>
					{optionsBlock}
					<div className={css(styleSheet.uploadContainer)}>
						<FilesDropTarget
							className={css(styleSheet.uploadContainer)}
							supportMultiple={false}
							supportedFileExtensions={AcceptedFileExtensions.split(',').map(x => x.slice(1))}
							title={uploadHint}
							onFilesDropped={this.onFileDropped}
						>
							<div className={css(styleSheet.uploadDropInner)}>
								<SpreadsheetIcon />
								<SvgIcon aria-hidden={true} height={27} width={20}>
									<polygon
										fill={brandSecondary}
										fillRule='evenodd'
										points='20 10.048 10 0 0 10.048 3.004 13.066 7.876 8.172 7.876 26.5 12.124 26.5 12.124 8.172 16.996 13.066'
									/>
								</SvgIcon>
								<input
									accept={AcceptedFileExtensions}
									className={css(styleSheet.hiddenFileInput)}
									multiple={false}
									onChange={this.onFilesInputChanged}
									ref={this.filesInputRef}
									type='file'
								/>
								<div className={css(styleSheet.uploadBrowseMessage)}>
									<span>Drop any Excel, CSV, or </span>
									<button className='brand-secondary-link' onClick={this.onChooseFileButtonClicked} type='button'>
										browse your files
									</button>
								</div>
							</div>
						</FilesDropTarget>
					</div>
				</div>
			);
		}

		return (
			<div className={css(styleSheet.modalContainer)}>
				<h2 className={css(styleSheet.header)}>Import Contacts</h2>
				<p>Click Import Contacts to continue, or choose a different file.</p>
				{optionsBlock}
				<div className={css(styleSheet.importContainer)}>
					<SpreadsheetIcon />

					<div>{this.state.csvFile ? this.state.csvFile.name : ''}</div>

					<span onClick={this.chooseDifferentFileClick} className='brand-secondary-link'>
						Choose a different file
					</span>

					<button className='cta-button-small' onClick={this.importClick}>
						Import Contacts
					</button>
				</div>
			</div>
		);
	}

	private chooseDifferentFileClick = () => {
		this.setState({ csvFile: null });
	};

	private importClick = () => {
		const { csvFile } = this.state;
		if (!csvFile) {
			return;
		}

		const fileExt = (csvFile.name || '')
			.split('.')
			.filter(x => !!x)
			.reverse()[0]
			.toLocaleLowerCase();

		switch (fileExt) {
			case 'xlsx': {
				this.mImportContacts.options.setContentType(ImportContactsContentType.xlsx);
				break;
			}
			case 'xls': {
				this.mImportContacts.options.setContentType(ImportContactsContentType.xls);
				break;
			}
			default: {
				this.mImportContacts.options.setContentType(ImportContactsContentType.csv);
				break;
			}
		}

		this.setState({ page: Page.Loader });

		const promise = this.mImportContacts.importFromFile(csvFile, undefined, this.state.dataRefresh);
		if (promise) {
			promise
				.then(() => {
					this.setState({ page: Page.Map });
				})
				.catch((error: IOperationResultNoValue) => {
					toast.error(error.systemMessage);
					this.setState({ page: Page.PickFile });
				});
		}
	};

	private importVisibilitySharedClick = () => {
		this.importVisibilityClick('all');
	};

	private importVisibilityPrivateClick = () => {
		this.importVisibilityClick('admin');
	};

	private importVisibilityClick = (visibility: string) => {
		this.mImportContacts.options.setVisibility(visibility);

		this.setState({
			page: Page.PickFile,
			visibility,
		});
	};

	private onImportClick = () => {
		this.setState({
			page: Page.Importing,
		});
	};

	private onCancel = () => {
		this.onClose();
	};

	private onSelectedTabIndexChanged = (selectedTabIndex: number) => {
		this.setState({
			selectedTabIndex,
		});
	};

	private getTabViewContent = (): ITabViewChild[] => {
		return tabViewItems.map<ITabViewChild>(x => {
			switch (x.dataContext) {
				case 'Import': {
					return {
						content: this.renderImportTab(),
						tabbarItem: x,
					};
				}
				case 'Convert': {
					return {
						content: <ConvertToLevitate />,
						tabbarItem: x,
					};
				}
				default: {
					break;
				}
			}
		});
	};

	private renderImportTab() {
		return (
			<div className={css(styleSheet.modalContainer)}>
				<p>Would you like these contacts to be shared or private?</p>
				<div className={css(styleSheet.actions)}>
					<button className='cta-button' onClick={this.importVisibilitySharedClick}>
						Shared
					</button>
					<button className='cta-button' onClick={this.importVisibilityPrivateClick}>
						Private
					</button>
				</div>
			</div>
		);
	}

	private onClose = () => {
		if (this.props.onRequestClose) {
			this.props.onRequestClose();
			this.setState({ selectedTabIndex: 0 });
		}
	};

	private onFileDropped = (files: File[]) => {
		if (files.length > 0) {
			this.setState({ csvFile: files[0] });
		}
	};

	private onChooseFileButtonClicked = () => {
		this.filesInputRef.current?.click();
	};

	private onFilesInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) {
			return;
		}
		const filteredFiles = Array.from(files).filter(x => {
			const comps = x.name ? x.name.split('.').filter(y => !!y) : [];
			const ext = comps.length > 1 ? comps[comps.length - 1] : null;
			return isAcceptedFileExtension(ext);
		});
		e.target.value = '';
		if (filteredFiles.length > 0) {
			this.setState({ csvFile: filteredFiles[0] });
		}
	};

	private onDataRefreshChange = (checked: boolean) => {
		this.setState({ dataRefresh: checked });
	};
}

const ContactImportAsObserver = observer(_ContactImport);
export const ContactImport = inject(UserSessionViewModelKey)(ContactImportAsObserver);
